// Toggle module
// Converts {{toggle: Title}} ... {{/toggle}} to collapsible sections

import { processTooltipReferences } from './footnotes.js';

/**
 * @param {Element}  container
 * @param {Map}      footnotesMap
 * @param {Function} recurse  - processAllSnippets(container, footnotesMap), passed to avoid circular import
 */
export function processToggleBlocks(container, footnotesMap, recurse) {
  const allElements = Array.from(container.children);
  const startRegex  = /\{\{toggle:\s*([^}]+)\}\}/;
  const endRegex    = /\{\{\/toggle\}\}/;

  const blocks = [];

  for (let i = 0; i < allElements.length; i++) {
    const element = allElements[i];
    const startMatch = (element.textContent || '').match(startRegex);
    if (!startMatch) continue;

    const title = startMatch[1].trim();
    let endIndex = -1, endElement = null;

    for (let j = i; j < allElements.length; j++) {
      if ((allElements[j].textContent || '').match(endRegex)) {
        endIndex = j; endElement = allElements[j]; break;
      }
    }

    if (endIndex === -1) {
      console.warn(`[Toggle] "{{toggle: ${title}}}" has no closing {{/toggle}}.`);
      continue;
    }

    blocks.push({ startIndex: i, endIndex, startElement: element, endElement, title });
    i = endIndex;
  }

  // Warn about orphaned closing markers
  allElements.forEach((el, idx) => {
    if ((el.textContent || '').match(endRegex)) {
      if (!blocks.some(b => b.endIndex === idx)) {
        console.warn('[Toggle] Found {{/toggle}} without a matching {{toggle: ...}}.');
      }
    }
  });

  // Process in reverse order to keep indices stable
  blocks.reverse().forEach(block => {
    if (!block.startElement?.parentNode || !block.endElement?.parentNode) return;

    const toggleDiv = document.createElement('div');
    toggleDiv.className = 'toggle';

    const headerBtn = document.createElement('button');
    headerBtn.className = 'toggle__header';
    headerBtn.setAttribute('type', 'button');
    headerBtn.setAttribute('aria-expanded', 'false');

    const titleSpan = document.createElement('span');
    titleSpan.className = 'toggle__title';
    titleSpan.textContent = block.title;
    headerBtn.appendChild(titleSpan);

    const iconSpan = document.createElement('span');
    iconSpan.className = 'toggle__icon';
    iconSpan.setAttribute('aria-hidden', 'true');
    headerBtn.appendChild(iconSpan);

    const contentDiv = document.createElement('div');
    contentDiv.className = 'toggle__content';

    // ── Collect content ────────────────────────────────────────────────────────
    const contentElements = [];

    if (block.startIndex === block.endIndex) {
      // Inline format: {{toggle: Title}}content{{/toggle}} all in one element
      const html = block.startElement.innerHTML || '';
      const inlineMatch = html.match(/\{\{toggle:\s*[^}]+\}\}\s*([\s\S]*?)\s*\{\{\/toggle\}\}/);
      if (inlineMatch?.[1]?.trim()) {
        const tmp = document.createElement('div');
        tmp.innerHTML = inlineMatch[1].trim();
        while (tmp.firstChild) contentElements.push(tmp.removeChild(tmp.firstChild));
      }
    } else {
      // Multi-line format: markers on separate elements
      const startHTML = block.startElement.innerHTML || '';
      const startHTMLMatch = startHTML.match(/\{\{toggle:\s*[^}]+\}\}\s*(.+)/s);
      if (startHTMLMatch?.[1]?.trim()) {
        const tmp = document.createElement('div');
        tmp.innerHTML = startHTMLMatch[1].trim();
        while (tmp.firstChild) contentElements.push(tmp.removeChild(tmp.firstChild));
      }

      // Re-query current DOM state (previous reverse iterations may have changed it)
      const currentChildren = Array.from(container.children);
      for (let k = block.startIndex + 1; k < block.endIndex && k < currentChildren.length; k++) {
        const mid = currentChildren[k];
        if (mid && mid.parentNode && mid !== block.startElement && mid !== block.endElement) {
          contentElements.push(mid.cloneNode(true));
        }
      }

      const endHTML = block.endElement.innerHTML || '';
      const endHTMLMatch = endHTML.match(/(.+?)\s*\{\{\/toggle\}\}/s);
      if (endHTMLMatch?.[1]?.trim()) {
        const tmp = document.createElement('div');
        tmp.innerHTML = endHTMLMatch[1].trim();
        const frag = document.createDocumentFragment();
        while (tmp.firstChild) frag.appendChild(tmp.firstChild);
        contentElements.push(...Array.from(frag.childNodes));
      }
    }

    contentElements.forEach(el => contentDiv.appendChild(el));
    toggleDiv.appendChild(headerBtn);
    toggleDiv.appendChild(contentDiv);

    block.startElement.replaceWith(toggleDiv);

    // Recurse into the new content container
    if (recurse) recurse(contentDiv, footnotesMap);
    else if (footnotesMap) processTooltipReferences(contentDiv, footnotesMap);

    if (block.startIndex !== block.endIndex) {
      // Remove middle + end elements (inline case has no separate end element)
      const afterReplace = Array.from(container.children);
      const newStart = afterReplace.indexOf(toggleDiv);
      if (newStart !== -1) {
        let endAfter = -1;
        for (let k = newStart + 1; k < afterReplace.length; k++) {
          if (afterReplace[k] === block.endElement) { endAfter = k; break; }
        }
        if (endAfter !== -1) {
          for (let k = endAfter; k > newStart; k--) {
            afterReplace[k]?.parentNode?.removeChild(afterReplace[k]);
          }
        } else {
          block.endElement?.parentNode?.removeChild(block.endElement);
        }
      }
    }

    // Click handler
    headerBtn.addEventListener('click', () => {
      const expanded = headerBtn.getAttribute('aria-expanded') === 'true';
      headerBtn.setAttribute('aria-expanded', String(!expanded));
      toggleDiv.classList.toggle('is-open', !expanded);
    });
  });
}
